import React from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '../../contexts/useAuth';
import { isAdmin } from '../../utils/authUtils';

interface AdminRouteProps {
  redirectPath?: string;
  fallbackComponent?: React.ComponentType;
}

/**
 * AdminRoute component that protects routes requiring admin privileges
 * Only allows access to users with Admin or SuperAdmin roles
 */
export const AdminRoute: React.FC<AdminRouteProps> = ({ 
  redirectPath = '/',
  fallbackComponent: FallbackComponent
}) => {
  const { user, isAuthenticated, isLoading } = useAuth();
  
  // Show loading spinner while authentication is being checked
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div 
          data-testid="loading-spinner"
          className="w-12 h-12 border-4 border-gray-300 rounded-full border-t-brand-500 animate-spin"
        ></div>
      </div>
    );
  }
  
  // If not authenticated, redirect to sign in
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // If authenticated but not an admin, show fallback or redirect
  if (!isAdmin(user)) {
    if (FallbackComponent) {
      return <FallbackComponent />;
    }
    
    return <Navigate to={redirectPath} replace />;
  }

  // If authenticated and is admin, render the child routes
  return <Outlet />;
};